import { __awaiter, __generator } from "tslib";
import { isFocusableHtmlElement } from '@office-start/dom-utility';
import { KnownInstrumentationEvents } from '@office-start/instrumentation-types';
import { fetchAndUpdateWorkloadPublicDataConfig } from '@office-start/workload-publicdata-config-service';
import { DimeState } from '../state';
import { logDimeEvent } from '../telemetry';
import { getCampainIdFromTargetUrl } from './../util';
import { loadDime } from './loadDime';
/**
 * launches targetUrl in a new tab in case Dime fails to load
 * @param targetUrl The targetUrl to launch in a new tab
 */
var launchTargetUrl = function (targetUrl) { return __awaiter(void 0, void 0, void 0, function () {
    var launcherService;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, import('@office-start/launcher-service')];
            case 1:
                launcherService = _a.sent();
                launcherService.launchUrl(targetUrl, { action: 'Upsell' });
                return [2 /*return*/];
        }
    });
}); };
/**
 * Launch dime or redirect to product page in case of error or timeout.
 */
export function launchDime(_a) {
    var _b;
    var dimeActionCreators = _a.dimeActionCreators, dimeExperience = _a.dimeExperience, dimeJSUrl = _a.dimeJSUrl, dimeTimeoutMs = _a.dimeTimeoutMs, preferredCulture = _a.preferredCulture, puid = _a.puid, pwaEnabled = _a.pwaEnabled, sessionId = _a.sessionId, targetUrl = _a.targetUrl, upn = _a.upn;
    return __awaiter(this, void 0, void 0, function () {
        var timeoutId, loadDimeTask, timeoutTask, status, ocid, launchConfig, activeElement_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (DimeState.dimeDisabled) {
                        logDimeEvent({
                            eventName: KnownInstrumentationEvents.DimeLaunch,
                            dimeExperience: dimeExperience,
                            result: 'Failed',
                            metaData: 'RedirectToPOC;DimeDisabled',
                        });
                        launchTargetUrl(targetUrl);
                        return [2 /*return*/];
                    }
                    timeoutId = null;
                    loadDimeTask = loadDime({
                        dimeJSUrl: dimeJSUrl,
                        puid: puid,
                        upn: upn,
                        pwaEnabled: pwaEnabled,
                        preferredCulture: preferredCulture,
                        sessionId: sessionId,
                    });
                    timeoutTask = new Promise(function (resolve) {
                        timeoutId = setTimeout(function () {
                            resolve(0 /* DimeTaskStatus.Timeout */);
                        }, dimeTimeoutMs);
                    });
                    return [4 /*yield*/, Promise.race([loadDimeTask, timeoutTask])];
                case 1:
                    status = _c.sent();
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                    }
                    // This is an edge case when dime is not loaded in 5 seconds. User will be redirect to POC when click again.
                    if (status !== 1 /* DimeTaskStatus.Completed */) {
                        DimeState.dimeDisabled = true;
                        logDimeEvent({
                            eventName: KnownInstrumentationEvents.DimeLaunch,
                            dimeExperience: dimeExperience,
                            result: 'Failed',
                            metaData: 'TimeOut',
                        });
                        return [2 /*return*/];
                    }
                    try {
                        ocid = getCampainIdFromTargetUrl(targetUrl);
                        launchConfig = {
                            experience: dimeExperience,
                            campaignId: ocid,
                        };
                        logDimeEvent({
                            eventName: KnownInstrumentationEvents.DimeLaunch,
                            dimeExperience: dimeExperience,
                            result: 'Start',
                        });
                        dimeActionCreators === null || dimeActionCreators === void 0 ? void 0 : dimeActionCreators.setDialogState('Open');
                        activeElement_1 = document.activeElement;
                        (_b = DimeState.dime) === null || _b === void 0 ? void 0 : _b.launch(launchConfig, function () {
                            logDimeEvent({
                                eventName: KnownInstrumentationEvents.DimeTransactionCompleted,
                                dimeExperience: dimeExperience,
                            });
                            dimeActionCreators === null || dimeActionCreators === void 0 ? void 0 : dimeActionCreators.setConvertedToPaid(true);
                            // Call workload public data to force refresh the CSS cache
                            fetchAndUpdateWorkloadPublicDataConfig(true)
                                .then(function (response) {
                                if (!response) {
                                    logDimeEvent({
                                        eventName: KnownInstrumentationEvents.DimeForceRefreshWorkloadPublicData,
                                        dimeExperience: dimeExperience,
                                        result: 'Failed',
                                        metaData: 'NoResponse',
                                    });
                                    return;
                                }
                                logDimeEvent({
                                    eventName: KnownInstrumentationEvents.DimeForceRefreshWorkloadPublicData,
                                    dimeExperience: dimeExperience,
                                    result: '',
                                    metaData: {
                                        hasBusinessLicense: response.hasBusinessLicense,
                                    },
                                });
                            })
                                .catch(function (error) {
                                logDimeEvent({
                                    eventName: KnownInstrumentationEvents.DimeForceRefreshWorkloadPublicData,
                                    dimeExperience: dimeExperience,
                                    result: 'Failed',
                                    metaData: error,
                                });
                            });
                        }).then(function (dimeResult) {
                            logDimeEvent({
                                eventName: KnownInstrumentationEvents.DimeCompleted,
                                dimeExperience: dimeExperience,
                                result: dimeResult === null || dimeResult === void 0 ? void 0 : dimeResult.status,
                                metaData: dimeResult === null || dimeResult === void 0 ? void 0 : dimeResult.metaData,
                            });
                            dimeActionCreators === null || dimeActionCreators === void 0 ? void 0 : dimeActionCreators.setDialogState('Closed');
                            if (isFocusableHtmlElement(activeElement_1)) {
                                activeElement_1 === null || activeElement_1 === void 0 ? void 0 : activeElement_1.focus();
                            }
                        });
                    }
                    catch (e) {
                        DimeState.dimeDisabled = true;
                        logDimeEvent({
                            eventName: KnownInstrumentationEvents.DimeLaunch,
                            dimeExperience: dimeExperience,
                            result: 'Failed',
                            metaData: e && typeof e === 'object' ? e : undefined,
                            extraInfo: 'RedirectToPOC;LaunchException',
                        });
                        launchTargetUrl(targetUrl);
                    }
                    return [2 /*return*/];
            }
        });
    });
}
//# sourceMappingURL=launchDime.js.map